/**
 * 인증 관련 API 서비스
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  User,
} from '../types/auth.types';
import { API_CONFIG, STORAGE_KEYS, ERROR_MESSAGES } from '../utils/constants';

/**
 * 인증 API 클라이언트 인스턴스
 */
const authApi: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API 에러 핸들러
 */
const handleApiError = (error: AxiosError): never => {
  if (error.response) {
    // 서버가 응답을 반환한 경우
    const status = error.response.status;
    // API 응답 구조: { success: false, code: 401, data: { code: "A010", message: "에러메시지", errors: [] } }
    const responseData = error.response.data as { message?: string; data?: { message?: string } };
    const message =
      responseData?.data?.message || responseData?.message || ERROR_MESSAGES.UNKNOWN_ERROR;

    switch (status) {
      case 401:
        // 401 에러 시 API message를 우선 사용, 없으면 기본 메시지
        throw new Error(message || ERROR_MESSAGES.UNAUTHORIZED);
      case 403:
        throw new Error(ERROR_MESSAGES.FORBIDDEN);
      case 404:
        throw new Error(ERROR_MESSAGES.NOT_FOUND);
      case 500:
      case 502:
      case 503:
        throw new Error(ERROR_MESSAGES.SERVER_ERROR);
      default:
        throw new Error(message);
    }
  } else if (error.request) {
    // 요청은 보냈지만 응답을 받지 못한 경우
    throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
  } else {
    // 요청 설정 중 에러가 발생한 경우
    throw new Error(ERROR_MESSAGES.UNKNOWN_ERROR);
  }
};

/**
 * 인증 서비스
 */
export const authService = {
  /**
   * 로그인
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      // API 실제 응답 구조
      interface ApiLoginResponse {
        success: boolean;
        code: number;
        data: {
          accessToken: string;
          fullName: string;
          userId?: number;
          refreshToken?: string;
          requireTermsAgreement?: boolean;
          requirePasswordChange?: boolean;
          passwordExpired?: boolean;
          daysUntilPasswordExpiry?: number;
        };
      }

      // Backend LoginRequestDto는 username과 password만 받음 (rememberMe 제외)
      const { username, password } = credentials;
      const response = await authApi.post<ApiLoginResponse>('/auth/login', { username, password });

      // code가 200이 아닌 경우 에러 처리 (HTTP 200이지만 비즈니스 로직 에러)
      // API 응답 구조: { success: false, code: 401, data: { code: "A010", message: "에러메시지", errors: [] } }
      if (response.data.code !== 200) {
        const errorMessage = response.data.data?.message || '로그인에 실패했습니다.';
        console.log(
          '❌ [authService] login failed - code:',
          response.data.code,
          'message:',
          errorMessage
        );
        throw new Error(errorMessage);
      }

      // API 응답을 LoginResponse 형식으로 변환
      const apiData = response.data.data;

      console.log('🔍 [authService] 로그인 API 응답:', response.data);
      console.log('🔍 [authService] apiData.userId:', apiData.userId);

      // JWT 토큰에서 역할 정보 추출
      let permissions: string[] = [];
      let userType: 'ADMIN' | 'MANAGER' | 'PARTNER' | 'DRIVER' = 'ADMIN';
      let roleId = 1;
      let roleName = '관리자';

      try {
        const tokenParts = apiData.accessToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.auth) {
            permissions =
              typeof payload.auth === 'string'
                ? payload.auth.split(',').map((r: string) => r.trim())
                : [];

            // 역할에 따른 userType 및 roleId 설정
            if (permissions.includes('ROLE_ADMIN')) {
              userType = 'ADMIN';
              roleId = 1;
              roleName = '슈퍼관리자';
            } else if (permissions.includes('ROLE_MANAGER')) {
              userType = 'MANAGER';
              roleId = 2;
              roleName = '관리자';
            } else if (permissions.includes('ROLE_DRIVER')) {
              userType = 'DRIVER';
              roleId = 3;
              roleName = '운전기사';
            } else if (permissions.includes('ROLE_PARTNER')) {
              userType = 'PARTNER';
              roleId = 4;
              roleName = '파트너';
            }
          }
          console.log('🔍 [authService] JWT에서 추출한 권한:', permissions);
        }
      } catch (e) {
        console.error('🔍 [authService] JWT 파싱 실패:', e);
      }

      // User 객체 생성
      const user: User = {
        id: apiData.userId || 0, // API에서 userId 사용
        username: credentials.username,
        fullName: apiData.fullName,
        email: credentials.username, // username이 email 형식이라고 가정
        userType,
        roleId,
        roleName,
        permissions,
        status: 'ACTIVE',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log('🔍 [authService] 생성된 User 객체:', user);

      console.log('🔍 [authService] loginResponse 생성 시작');
      const loginResponse: LoginResponse = {
        user,
        accessToken: apiData.accessToken,
        refreshToken: apiData.refreshToken || '', // refreshToken이 없으면 빈 문자열
        expiresIn: 3600, // 기본값 1시간
        requireTermsAgreement: apiData.requireTermsAgreement, // 약관 동의 필요 여부
        requirePasswordChange: apiData.requirePasswordChange, // 비밀번호 변경 필요 여부
        passwordExpired: apiData.passwordExpired, // 비밀번호 만료 여부
        daysUntilPasswordExpiry: apiData.daysUntilPasswordExpiry, // 만료까지 남은 일수
      };
      console.log('🔍 [authService] loginResponse 생성됨:', loginResponse);

      // 토큰 저장
      console.log('🔍 [authService] 토큰 저장 시작, rememberMe:', credentials.rememberMe);
      console.log('🔍 [authService] STORAGE_KEYS:', STORAGE_KEYS);
      console.log('🔍 [authService] accessToken:', loginResponse.accessToken);
      console.log('🔍 [authService] refreshToken:', loginResponse.refreshToken);

      if (credentials.rememberMe) {
        console.log('🔍 [authService] localStorage에 저장');
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginResponse.accessToken);
        console.log('🔍 [authService] localStorage.setItem 완료:', STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginResponse.refreshToken);
        console.log('🔍 [authService] localStorage.setItem 완료:', STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        console.log('🔍 [authService] localStorage.setItem 완료:', STORAGE_KEYS.USER);
      } else {
        console.log('🔍 [authService] sessionStorage에 저장');
        console.log(
          '🔍 [authService] sessionStorage.setItem 시작:',
          STORAGE_KEYS.ACCESS_TOKEN,
          loginResponse.accessToken.substring(0, 20) + '...'
        );
        sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginResponse.accessToken);
        console.log(
          '🔍 [authService] sessionStorage.setItem 완료, 확인:',
          sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)?.substring(0, 20)
        );

        sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginResponse.refreshToken);
        console.log('🔍 [authService] sessionStorage.setItem 완료:', STORAGE_KEYS.REFRESH_TOKEN);

        sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        console.log('🔍 [authService] sessionStorage.setItem 완료:', STORAGE_KEYS.USER);
        console.log(
          '🔍 [authService] sessionStorage 최종 확인 - dream_main_token:',
          sessionStorage.getItem('dream_main_token')?.substring(0, 20)
        );
      }
      console.log('🔍 [authService] 토큰 저장 완료');

      console.log('🔍 [authService] loginResponse 반환');
      return loginResponse;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 로그아웃
   */
  logout: async (): Promise<void> => {
    try {
      const accessToken =
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (accessToken) {
        await authApi.post(
          '/auth/logout',
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
      }
    } catch (error) {
      // 로그아웃 에러는 무시하고 로컬 스토리지만 정리
      console.error('Logout error:', error);
    } finally {
      // 로컬 스토리지 정리
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.USER);
    }
  },

  /**
   * 토큰 갱신
   */
  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    try {
      const response = await authApi.post<RefreshTokenResponse>('/auth/refresh', {
        refreshToken,
      } as RefreshTokenRequest);

      // 새 토큰 저장
      const isLocalStorage = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storage = isLocalStorage ? localStorage : sessionStorage;

      storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
      storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);

      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 현재 사용자 정보 조회
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const accessToken =
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      interface ApiMeResponse {
        success: boolean;
        code: number;
        data: {
          id: number;
          username: string;
          fullName: string;
          email: string;
          phoneNumber?: string;
          userType: 'ADMIN' | 'MANAGER' | 'PARTNER' | 'DRIVER';
          roleId: number;
          roleName?: string;
          workplaceId?: number;
          workplaceName?: string;
          permissions?: string[];
          status?: string;
        };
      }

      const response = await authApi.get<ApiMeResponse>('/auth/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const apiData = response.data.data;
      const user: User = {
        id: apiData.id,
        username: apiData.username,
        fullName: apiData.fullName,
        email: apiData.email,
        phoneNumber: apiData.phoneNumber,
        userType: apiData.userType,
        roleId: apiData.roleId,
        roleName: apiData.roleName,
        workplaceId: apiData.workplaceId,
        workplaceName: apiData.workplaceName,
        permissions: apiData.permissions,
        status: 'ACTIVE',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // 사용자 정보 업데이트
      const storage = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
        ? localStorage
        : sessionStorage;
      storage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

      return user;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 아이디 찾기
   */
  findUsername: async (
    fullName: string,
    phoneNumber: string
  ): Promise<{ maskedUsername: string; success: boolean }> => {
    try {
      interface ApiFindUsernameResponse {
        success: boolean;
        code: number;
        data: {
          maskedUsername: string;
          success: boolean;
        };
      }

      const response = await authApi.post<ApiFindUsernameResponse>('/auth/find-username', {
        fullName,
        phoneNumber,
      });

      return response.data.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 비밀번호 찾기 (임시 비밀번호 SMS 발송)
   */
  findPassword: async (
    username: string,
    phoneNumber: string
  ): Promise<{ success: boolean; message: string; maskedPhoneNumber: string }> => {
    try {
      interface ApiFindPasswordResponse {
        success: boolean;
        code: number;
        data: {
          success: boolean;
          message: string;
          maskedPhoneNumber: string;
        };
      }

      const response = await authApi.post<ApiFindPasswordResponse>('/auth/find-password', {
        username,
        phoneNumber,
      });

      return response.data.data;
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 비밀번호 찾기 (이메일 전송) - 레거시
   */
  forgotPassword: async (request: ForgotPasswordRequest): Promise<void> => {
    try {
      await authApi.post('/auth/forgot-password', request);
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 비밀번호 재설정
   */
  resetPassword: async (request: ResetPasswordRequest): Promise<void> => {
    try {
      await authApi.post('/auth/reset-password', request);
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 비밀번호 변경
   */
  changePassword: async (request: ChangePasswordRequest): Promise<void> => {
    try {
      const accessToken =
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      await authApi.post('/auth/change-password', request, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      throw handleApiError(error as AxiosError);
    }
  },

  /**
   * 저장된 토큰 조회
   */
  getStoredTokens: (): {
    accessToken: string | null;
    refreshToken: string | null;
  } => {
    return {
      accessToken:
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
      refreshToken:
        localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
        sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    };
  },

  /**
   * 저장된 사용자 정보 조회
   */
  getStoredUser: (): User | null => {
    const userStr =
      localStorage.getItem(STORAGE_KEYS.USER) || sessionStorage.getItem(STORAGE_KEYS.USER);

    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  },
};

/**
 * Axios 인터셉터 설정
 * - 요청 시 자동으로 토큰 추가
 * - 401 에러 시 자동 토큰 갱신 시도
 */
authApi.interceptors.request.use(
  config => {
    const accessToken =
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

authApi.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // 401 에러이고 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { refreshToken } = authService.getStoredTokens();

        if (refreshToken) {
          // 토큰 갱신 시도
          const response = await authService.refreshToken(refreshToken);

          // 새 토큰으로 원래 요청 재시도
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
          }

          return authApi(originalRequest);
        }
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃
        await authService.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default authService;
